import { PropertyType } from "@/types/types";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface BasicInfoFormProps {
  values: {
    title?: string;
    address?: string;
    type?: PropertyType;
  };
  onChange: (values: {
    title?: string;
    address?: string;
    type?: PropertyType;
  }) => void;
}

const PROPERTY_TYPES: {
  value: PropertyType;
  label: string;
  description: string;
}[] = [
  {
    value: "apartment",
    label: "Apartment",
    description: "Unit in a residential building",
  },
  { value: "flat", label: "Flat", description: "Self-contained housing unit" },
  { value: "duplex", label: "Duplex", description: "Two-floor residence" },
  { value: "bungalow", label: "Bungalow", description: "Single-story house" },
  {
    value: "detached",
    label: "Detached House",
    description: "Standalone house",
  },
  {
    value: "semi-detached",
    label: "Semi-Detached",
    description: "House sharing a wall",
  },
  { value: "terrace", label: "Terrace", description: "Row house" },
  {
    value: "penthouse",
    label: "Penthouse",
    description: "Luxury top-floor unit",
  },
  { value: "studio", label: "Studio", description: "Open-plan living space" },
  { value: "mansion", label: "Mansion", description: "Large luxury home" },
  {
    value: "estate-house",
    label: "Estate House",
    description: "House in gated estate",
  },
  {
    value: "self-contain",
    label: "Self-Contain",
    description: "Single room with facilities",
  },
  { value: "mini-flat", label: "Mini-Flat", description: "Small apartment" },
  {
    value: "boys-quarters",
    label: "Boys Quarters",
    description: "Servant quarters/annex",
  },
];

export default function BasicInfoForm({
  values,
  onChange,
}: BasicInfoFormProps) {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-semibold text-foreground">
          Basic Information
        </h2>
        <p className="mt-2 text-base text-muted-foreground">
          Tell us about your property's name, location, and type
        </p>
      </div>

      <div className="space-y-6">
        <div className="flex gap-6">
          {/* Property Name */}
        <div className="space-y-2 flex-2">
          <Label htmlFor="title" className="text-base font-medium">
            Property Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="title"
            type="text"
            placeholder="e.g., Luxury 3BR in Lekki Phase 1"
            value={values.title || ""}
            onChange={(e) => onChange({ ...values, title: e.target.value })}
            className="h-12 text-base"
            required
          />
          <p className="text-sm text-muted-foreground">
            Give your property a descriptive name that attracts tenants
          </p>
        </div>
        {/* Property Type */}
        <div className="space-y-2">
          <Label htmlFor="type" className="text-base font-medium">
            Property Type <span className="text-red-500">*</span>
          </Label>
          <Select
            value={values.type}
            onValueChange={(value: PropertyType) =>
              onChange({ ...values, type: value })
            }
          >
            <SelectTrigger className="h-12 w-full text-base bg-background">
              <SelectValue placeholder="Select property type" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-black">
              {PROPERTY_TYPES.map((type) => (
                <SelectItem
                  key={type.value}
                  value={type.value}
                  className="focus:bg-accent focus:text-accent-foreground"
                >
                  <div className="flex flex-col items-start py-1">
                    <span className="font-medium">{type.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {type.description}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            Choose the type that best describes your property
          </p>
        </div>
        </div>

        {/* Property Address */}
        <div className="space-y-2">
          <Label htmlFor="address" className="text-base font-medium">
            Property Address <span className="text-red-500">*</span>
          </Label>
          <Input
            id="address"
            type="text"
            placeholder="e.g., 15 Admiralty Way, Lekki Phase 1, Lagos"
            value={values.address || ""}
            onChange={(e) => onChange({ ...values, address: e.target.value })}
            className="h-12 text-base"
            required
          />
          <p className="text-sm text-muted-foreground">
            Enter the complete address including street, area, and state
          </p>
        </div>
      </div>

      {/* Preview Card */}
      {(values.title || values.address || values.type) && (
        <div className="mt-8 p-6 bg-muted/50 border rounded-xl">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-3">
            Preview
          </h3>
          <div className="space-y-2">
            {values.title && (
              <div>
                <span className="text-sm text-muted-foreground">
                  Property Name:
                </span>
                <p className="text-base font-medium text-foreground">
                  {values.title}
                </p>
              </div>
            )}
            {values.address && (
              <div>
                <span className="text-sm text-muted-foreground">Address:</span>
                <p className="text-base text-foreground">{values.address}</p>
              </div>
            )}
            {values.type && (
              <div>
                <span className="text-sm text-muted-foreground">Type:</span>
                <p className="text-base text-foreground">
                  {PROPERTY_TYPES.find((t) => t.value === values.type)?.label}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
